import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Trophy, ArrowRight } from "lucide-react";
import { FadeUp } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const rates = [
  { tier: "Hobbyist", regular: 4.99, founding: 2.99 },
  { tier: "Apiary Pro", regular: 19.99, founding: 12.99 },
];

export default function FoundingKeeper() {
  const [spots, setSpots] = useState(null);

  useEffect(() => {
    axios.get(`${API}/settings/public`).then((r) => setSpots(r.data)).catch(() => {});
  }, []);

  const remaining = spots ? Math.max(spots.founding_total - spots.founding_claimed, 0) : null;

  return (
    <section className="bg-forest-deep text-bone relative overflow-hidden" data-testid="founding-keeper-section">
      <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[42rem] h-[42rem] rounded-full bg-honey/10 blur-3xl pointer-events-none" />
      <div className="max-w-5xl mx-auto px-6 md:px-10 py-24 md:py-32 relative text-center">
        <FadeUp>
          <Trophy className="w-10 h-10 text-honey mx-auto mb-6" strokeWidth={1.5} />
          <h2 className="font-display text-4xl md:text-6xl font-medium tracking-tighter leading-[1.02] mb-6" data-testid="founding-keeper-headline">
            Become a <em className="text-honey">Founding Keeper</em> 🏆
          </h2>
          <p className="text-bone/70 max-w-2xl mx-auto leading-relaxed mb-4">
            Only 100 spots available. Founding Keepers lock in{" "}
            <span className="text-honey font-medium">$2.99/month for Hobbyist</span> or{" "}
            <span className="text-honey font-medium">$12.99/month for Apiary Pro</span> — forever.
            Plus: a Founding Keeper badge, 50 BeeCredits™ on launch day, double referral rewards,
            early access to every new feature, and your name in our Founding Keepers Hall if you choose.
          </p>
          {remaining !== null && (
            <p className="inline-block bg-honey/15 border border-honey/40 text-honey text-sm font-semibold px-5 py-2 rounded-full mb-10" data-testid="founding-spots-counter">
              {remaining} of {spots.founding_total} spots remaining
            </p>
          )}
        </FadeUp>

        <div className="grid sm:grid-cols-2 gap-6 max-w-2xl mx-auto mb-12">
          {rates.map((r, i) => (
            <FadeUp key={r.tier} delay={0.1 + i * 0.1}>
              <div className="bg-bone/5 border border-honey/30 rounded-2xl p-6" data-testid={`founding-rate-${r.tier.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}>
                <p className="text-xs uppercase tracking-[0.2em] text-bone/50 mb-3">{r.tier}</p>
                <div className="flex items-baseline justify-center gap-2">
                  <span className="font-display text-4xl font-semibold text-honey">${r.founding}</span>
                  <span className="text-bone/50 text-sm">/mo</span>
                  <span className="text-bone/40 line-through text-sm ml-1">${r.regular}/mo</span>
                </div>
                <p className="text-xs text-honey/80 mt-2">
                  Save ${(r.regular - r.founding).toFixed(2)}/mo — locked forever
                </p>
              </div>
            </FadeUp>
          ))}
        </div>

        <FadeUp delay={0.3}>
          <Link
            to="/pre-register?src=founding"
            data-testid="founding-keeper-cta"
            className="group inline-flex items-center gap-2 bg-honey text-forest font-semibold px-10 py-4 rounded-full transition-colors duration-300 hover:bg-honey-light"
          >
            Claim Your Founding Keeper Spot
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </FadeUp>
      </div>
    </section>
  );
}
